/**
 * ====================================================================
 * FIKO CAD CHAIN DIMENSION
 * ====================================================================
 *
 * Zincir ölçü - Ardışık noktalar arasındaki mesafeleri gösterir.
 * CATIA/AutoCAD'in "Continue Dimension" özelliğine karşılık gelir.
 *
 * Özellikler:
 * - Uç uca bağlı ölçüler
 * - Paylaşılan extension line'lar
 * - Toplam ölçü gösterimi (opsiyonel)
 * - Dinamik nokta ekleme/çıkarma
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    GeometryReference,
    BaseDimensionOptions,
    AxisLock,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

// ====================================================================
// CHAIN DIMENSION OPTIONS
// ====================================================================

/**
 * Chain dimension için özel seçenekler
 */
export interface ChainDimensionOptions extends BaseDimensionOptions {
    /** Eksen kilidi */
    axisLock?: AxisLock;
    /** Toplam ölçü göster */
    showTotal?: boolean;
    /** Toplam ölçü pozisyonu (offset) */
    totalOffset?: number;
    /** Segment numaraları göster */
    showSegmentNumbers?: boolean;
    /** Minimum segment uzunluğu (görüntülemede birleştir) */
    minSegmentLength?: number;
}

/**
 * Chain segment bilgisi
 */
export interface ChainSegment {
    startPoint: THREE.Vector3;
    endPoint: THREE.Vector3;
    distance: number;
    index: number;
}

// ====================================================================
// CHAIN DIMENSION CLASS
// ====================================================================

/**
 * Chain Dimension - Zincir ölçü
 */
export class ChainDimension extends Dimension {
    // Noktalar
    private points: THREE.Vector3[] = [];
    private segments: ChainSegment[] = [];
    private totalDistance: number = 0;

    // Çizim elemanları
    private dimensionLines: LineSegments2[] = [];
    private extLines: LineSegments2[] = [];
    private arrowMeshes: THREE.Mesh[] = [];
    private labels: THREE.Sprite[] = [];
    private totalLabel: THREE.Sprite | null = null;

    // Pre-allocated
    private readonly _tempVec = new THREE.Vector3();
    private readonly _direction = new THREE.Vector3();

    // Shared geometries
    private static _sharedArrowGeometry: THREE.BufferGeometry | null = null;

    // Sahne referansı
    private scene: FikoSahne;

    // SABİT BOYUT
    sabitBoyut = true;
    private hazirlaListenerEklendi = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // PERFORMANS
    private lastKameraUpdateTime = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS = 100;
    private targetScaleAdjustment = 1;
    private currentScaleAdjustment = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        points: THREE.Vector3[],
        options: ChainDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.CHAIN, options);

        this.scene = scene;

        // Noktaları kopyala
        for (const p of points) {
            this.points.push(p.clone());
        }
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Minimum 2 nokta gerekli
            if (this.points.length < 2) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'En az 2 nokta gerekli'
                    }
                };
            }

            // Segmentleri hesapla
            this.calculateSegments();

            // Toplam mesafe
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.COINCIDENT_POINTS,
                        message: 'Noktalar çakışıyor'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // Sahneye hazırla
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Chain dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Segmentleri hesapla
     */
    private calculateSegments(): void {
        this.segments = [];
        this.totalDistance = 0;

        const options = this._options as ChainDimensionOptions;
        const axisLock = options.axisLock || AxisLock.NONE;

        for (let i = 0; i < this.points.length - 1; i++) {
            const start = this.points[i];
            const end = this.points[i + 1];

            let distance: number;

            switch (axisLock) {
                case AxisLock.X:
                    distance = Math.abs(end.x - start.x);
                    break;
                case AxisLock.Y:
                    distance = Math.abs(end.y - start.y);
                    break;
                case AxisLock.Z:
                    distance = Math.abs(end.z - start.z);
                    break;
                case AxisLock.XY_PLANE:
                    distance = Math.sqrt(
                        Math.pow(end.x - start.x, 2) +
                        Math.pow(end.y - start.y, 2)
                    );
                    break;
                case AxisLock.XZ_PLANE:
                    distance = Math.sqrt(
                        Math.pow(end.x - start.x, 2) +
                        Math.pow(end.z - start.z, 2)
                    );
                    break;
                case AxisLock.YZ_PLANE:
                    distance = Math.sqrt(
                        Math.pow(end.y - start.y, 2) +
                        Math.pow(end.z - start.z, 2)
                    );
                    break;
                default:
                    distance = start.distanceTo(end);
            }

            this.segments.push({
                startPoint: start.clone(),
                endPoint: end.clone(),
                distance,
                index: i
            });

            this.totalDistance += distance;
        }
    }

    /**
     * Dimension'ı güncelle
     */
    update(): void {
        // Scale güncelleme event listener üzerinden yapılır
    }

    /**
     * Sahneye hazırlama
     */
    hazirla(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        try {
            if (this.labels.length > 0 && this.labels[0]?.userData.originalScale) {
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Sessizce devam et
        }
    }

    /**
     * Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) return;

        if (now - this.lastKameraUpdateTime < ChainDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, ChainDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * Kamera değişikliğinde scale güncelle
     */
    private kameraYonDegisti(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        if (this.sabitBoyut) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

            this.targetScaleAdjustment = factor * 0.02;
            this.startSmoothInterpolation();
        }
    }

    /**
     * Smooth scale interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.sabitBoyut || (this.labels.length === 0 && !this.totalLabel)) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            // Segment labels
            for (const label of this.labels) {
                if (label?.userData.originalScale) {
                    const originalScale = label.userData.originalScale as THREE.Vector3;
                    label.scale.set(
                        originalScale.x * this.currentScaleAdjustment,
                        originalScale.y * this.currentScaleAdjustment,
                        1
                    );
                }
            }

            // Total label
            if (this.totalLabel?.userData.originalScale) {
                const originalScale = this.totalLabel.userData.originalScale as THREE.Vector3;
                this.totalLabel.scale.set(
                    originalScale.x * this.currentScaleAdjustment,
                    originalScale.y * this.currentScaleAdjustment,
                    1
                );
            }

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        return this._value >= DIMENSION_CONSTANTS.MIN_DISTANCE;
    }

    /**
     * Değeri hesapla (toplam mesafe)
     */
    protected calculateValue(): number {
        return this.totalDistance;
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as ChainDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;
        const offset = options.offset || DIMENSION_CONSTANTS.DEFAULT_OFFSET;
        const showTotal = options.showTotal !== false;
        const totalOffset = options.totalOffset || offset * 2;
        const arrowSize = options.arrowSize || DIMENSION_CONSTANTS.DEFAULT_ARROW_SIZE;

        if (this.segments.length === 0) return;

        // Genel yön hesapla (ilk ve son nokta arası)
        const firstPoint = this.points[0];
        const lastPoint = this.points[this.points.length - 1];
        this._direction.subVectors(lastPoint, firstPoint).normalize();

        // Offset yönü (zincir çizgisine dik)
        let offsetDir = new THREE.Vector3(0, 1, 0);
        if (Math.abs(this._direction.dot(offsetDir)) > 0.9) {
            offsetDir.set(1, 0, 0);
        }
        offsetDir.crossVectors(this._direction, offsetDir).normalize();

        // Her segment için
        for (const segment of this.segments) {
            const start = segment.startPoint.clone().addScaledVector(offsetDir, offset);
            const end = segment.endPoint.clone().addScaledVector(offsetDir, offset);

            // Dimension line
            const dimLine = this.createLine(
                [start.x, start.y, start.z, end.x, end.y, end.z],
                color, lineWidth
            );
            dimLine.name = `DimLine_${segment.index}`;
            this.dimensionLines.push(dimLine);
            this.add(dimLine);

            // Extension lines
            const ext1 = this.createLine(
                [
                    segment.startPoint.x, segment.startPoint.y, segment.startPoint.z,
                    start.x, start.y, start.z
                ],
                color, lineWidth * 0.7, 0.6
            );
            ext1.name = `ExtLine_${segment.index}_start`;
            this.extLines.push(ext1);
            this.add(ext1);

            // Son segment veya bağımsız extension line
            if (segment.index === this.segments.length - 1) {
                const ext2 = this.createLine(
                    [
                        segment.endPoint.x, segment.endPoint.y, segment.endPoint.z,
                        end.x, end.y, end.z
                    ],
                    color, lineWidth * 0.7, 0.6
                );
                ext2.name = `ExtLine_${segment.index}_end`;
                this.extLines.push(ext2);
                this.add(ext2);
            }

            // Oklar
            const segmentDir = new THREE.Vector3().subVectors(end, start).normalize();

            const arrowStart = this.createArrow(start, segmentDir, color, arrowSize);
            arrowStart.name = `Arrow_${segment.index}_start`;
            this.arrowMeshes.push(arrowStart);
            this.add(arrowStart);

            const arrowEnd = this.createArrow(end, segmentDir.clone().negate(), color, arrowSize);
            arrowEnd.name = `Arrow_${segment.index}_end`;
            this.arrowMeshes.push(arrowEnd);
            this.add(arrowEnd);

            // Label (segment ortasında)
            const labelPos = start.clone().lerp(end, 0.5);
            labelPos.addScaledVector(offsetDir, options.textOffset || 1);

            const labelText = this.formatValue(segment.distance);
            const label = this.createLabel(labelText, labelPos, options.textColor || '#000000');
            label.name = `Label_${segment.index}`;
            this.labels.push(label);
            this.add(label);
        }

        // Toplam ölçü
        if (showTotal && this.segments.length > 1) {
            const totalStart = this.points[0].clone().addScaledVector(offsetDir, totalOffset);
            const totalEnd = this.points[this.points.length - 1].clone().addScaledVector(offsetDir, totalOffset);

            // Toplam dimension line
            const totalDimLine = this.createLine(
                [totalStart.x, totalStart.y, totalStart.z, totalEnd.x, totalEnd.y, totalEnd.z],
                0x0066FF, lineWidth * 1.2
            );
            totalDimLine.name = 'TotalDimLine';
            this.dimensionLines.push(totalDimLine);
            this.add(totalDimLine);

            // Toplam extension lines
            const totalExt1 = this.createLine(
                [
                    this.points[0].x, this.points[0].y, this.points[0].z,
                    totalStart.x, totalStart.y, totalStart.z
                ],
                0x0066FF, lineWidth * 0.5, 0.4
            );
            this.extLines.push(totalExt1);
            this.add(totalExt1);

            const totalExt2 = this.createLine(
                [
                    this.points[this.points.length - 1].x,
                    this.points[this.points.length - 1].y,
                    this.points[this.points.length - 1].z,
                    totalEnd.x, totalEnd.y, totalEnd.z
                ],
                0x0066FF, lineWidth * 0.5, 0.4
            );
            this.extLines.push(totalExt2);
            this.add(totalExt2);

            // Toplam oklar
            const totalDir = new THREE.Vector3().subVectors(totalEnd, totalStart).normalize();

            const totalArrow1 = this.createArrow(totalStart, totalDir, 0x0066FF, arrowSize * 1.2);
            this.arrowMeshes.push(totalArrow1);
            this.add(totalArrow1);

            const totalArrow2 = this.createArrow(totalEnd, totalDir.clone().negate(), 0x0066FF, arrowSize * 1.2);
            this.arrowMeshes.push(totalArrow2);
            this.add(totalArrow2);

            // Toplam label
            const totalLabelPos = totalStart.clone().lerp(totalEnd, 0.5);
            totalLabelPos.addScaledVector(offsetDir, options.textOffset || 1);

            const totalText = `Σ ${this.formatValue(this.totalDistance)}`;
            this.totalLabel = this.createLabel(totalText, totalLabelPos, '#0066FF');
            this.totalLabel.name = 'TotalLabel';
            this.add(this.totalLabel);
        }
    }

    /**
     * Çizgi oluştur
     */
    private createLine(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): LineSegments2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false,
            toneMapped: false
        });

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Ok oluştur
     */
    private createArrow(position: THREE.Vector3, direction: THREE.Vector3, color: number, size: number): THREE.Mesh {
        if (!ChainDimension._sharedArrowGeometry) {
            ChainDimension._sharedArrowGeometry = new THREE.ConeGeometry(0.3, 0.8, 8);
            ChainDimension._sharedArrowGeometry.translate(0, -0.4, 0);
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const arrow = new THREE.Mesh(ChainDimension._sharedArrowGeometry, material);
        arrow.position.copy(position);
        arrow.scale.setScalar(size);

        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        arrow.quaternion.copy(quaternion);

        arrow.renderOrder = Infinity;
        arrow.frustumCulled = false;

        return arrow;
    }

    /**
     * Label oluştur
     */
    private createLabel(text: string, position: THREE.Vector3, textColor: string): THREE.Sprite {
        const fontSize = this._options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 4;

        const padding = 14;
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        // Arka plan
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.roundRect(3, 3, canvas.width - 6, canvas.height - 6, 4);
        ctx.fill();

        // Metin
        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.shadowBlur = 2;
        ctx.fillStyle = textColor;
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        sprite.scale.set(canvas.width * 0.01, canvas.height * 0.01, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        for (const line of this.dimensionLines) {
            (line.material as LineMaterial).color.setHex(color);
        }

        for (const arrow of this.arrowMeshes) {
            (arrow.material as THREE.MeshBasicMaterial).color.setHex(color);
        }
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        for (const line of this.dimensionLines) {
            this.remove(line);
            line.geometry.dispose();
            (line.material as LineMaterial).dispose();
        }
        this.dimensionLines = [];

        for (const line of this.extLines) {
            this.remove(line);
            line.geometry.dispose();
            (line.material as LineMaterial).dispose();
        }
        this.extLines = [];

        for (const arrow of this.arrowMeshes) {
            this.remove(arrow);
            (arrow.material as THREE.Material).dispose();
        }
        this.arrowMeshes = [];

        for (const label of this.labels) {
            this.remove(label);
            label.material.map?.dispose();
            label.material.dispose();
        }
        this.labels = [];

        if (this.totalLabel) {
            this.remove(this.totalLabel);
            this.totalLabel.material.map?.dispose();
            this.totalLabel.material.dispose();
            this.totalLabel = null;
        }
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * Nokta ekle
     */
    addPoint(point: THREE.Vector3): void {
        this.points.push(point.clone());
        this.calculateSegments();
        this._value = this.totalDistance;
        this.buildGeometry();
    }

    /**
     * Son noktayı kaldır
     */
    removeLastPoint(): boolean {
        if (this.points.length <= 2) return false;

        this.points.pop();
        this.calculateSegments();
        this._value = this.totalDistance;
        this.buildGeometry();
        return true;
    }

    /**
     * Tüm noktaları al
     */
    getPoints(): THREE.Vector3[] {
        return this.points.map(p => p.clone());
    }

    /**
     * Segment'leri al
     */
    getSegments(): ChainSegment[] {
        return this.segments.map(s => ({
            ...s,
            startPoint: s.startPoint.clone(),
            endPoint: s.endPoint.clone()
        }));
    }

    /**
     * Toplam mesafeyi al
     */
    getTotalDistance(): number {
        return this.totalDistance;
    }

    /**
     * Segment sayısını al
     */
    getSegmentCount(): number {
        return this.segments.length;
    }

    /**
     * Dispose
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
